import React, { useEffect, useState } from 'react';
import { Plus, Search, Download, Eye, Edit, Trash2, Calendar, MapPin, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { ApiService } from '../services/api';
import { Booking, BookingStatus, PaymentStatus } from '../types';
import toast from 'react-hot-toast';

export const Bookings: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<BookingStatus | 'all'>('all');
  const [paymentFilter, setPaymentFilter] = useState<PaymentStatus | 'all'>('all');
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showBookingDetail, setShowBookingDetail] = useState(false);
  const [nextCursor, setNextCursor] = useState<Date | null>(null);
  const [hasNext, setHasNext] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    loadBookings();
  }, []);

  useEffect(() => {
    filterBookings();
  }, [bookings, searchTerm, statusFilter, paymentFilter]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const result = await ApiService.getBookings({ page: 1, limit: 50 });
      setBookings(result.data);
      setHasNext(result.pagination.hasNext);
      const last = result.data[result.data.length - 1];
      setNextCursor(last ? last.createdAt : null);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!hasNext || !nextCursor) return;
    try {
      setLoadingMore(true);
      const result = await ApiService.getBookings({
        page: 1,
        limit: 50,
        startAfterDate: nextCursor,
      });
      setBookings(prev => [...prev, ...result.data]);
      setHasNext(result.pagination.hasNext);
      const last = result.data[result.data.length - 1];
      setNextCursor(last ? last.createdAt : nextCursor);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load more bookings');
    } finally {
      setLoadingMore(false);
    }
  };

  const filterBookings = () => {
    let filtered = bookings;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(booking =>
        booking.passengerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.passengerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.bookingReference.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.bookingStatus === statusFilter);
    }

    // Payment filter
    if (paymentFilter !== 'all') {
      filtered = filtered.filter(booking => booking.paymentStatus === paymentFilter);
    }

    setFilteredBookings(filtered);
  };

  const updateBookingStatus = async (bookingId: string, status: BookingStatus) => {
    try {
      const result = await ApiService.updateBooking(bookingId, { bookingStatus: status });

      if (result.success) {
        setBookings(bookings.map(booking =>
          booking.id === bookingId ? { ...booking, bookingStatus: status } : booking
        ));
        toast.success('Booking status updated successfully');
      } else {
        toast.error(result.error || 'Failed to update booking status');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update booking status');
    }
  };

  const updatePaymentStatus = async (bookingId: string, status: PaymentStatus) => {
    try {
      const result = await ApiService.updateBooking(bookingId, { paymentStatus: status });

      if (result.success) {
        setBookings(bookings.map(booking =>
          booking.id === bookingId ? { ...booking, paymentStatus: status } : booking
        ));
        toast.success('Payment status updated successfully');
      } else {
        toast.error(result.error || 'Failed to update payment status');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update payment status');
    }
  };

  const deleteBooking = async (bookingId: string) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) {
      return;
    }

    try {
      const result = await ApiService.deleteBooking(bookingId);

      if (result.success) {
        setBookings(bookings.filter(booking => booking.id !== bookingId));
        toast.success('Booking deleted successfully');
      } else {
        toast.error(result.error || 'Failed to delete booking');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete booking');
    }
  };

  const exportBookings = () => {
    const csvContent = [
      ['Booking Reference', 'Passenger Name', 'Email', 'Phone', 'Route', 'Travel Date', 'Status', 'Payment', 'Amount'],
      ...filteredBookings.map(booking => [
        booking.bookingReference,
        booking.passengerName,
        booking.passengerEmail,
        booking.passengerPhone,
        `${booking.route?.fromCity} → ${booking.route?.toCity}`,
        new Date(booking.travelDate).toLocaleDateString(),
        booking.bookingStatus,
        booking.paymentStatus,
        `₵${(booking.totalAmount || 0).toFixed(2)}`
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookings-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast.success('Bookings exported successfully');
  };

  const getStatusColor = (status: BookingStatus) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentColor = (status: PaymentStatus) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'refunded': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Bookings</h1>
          <p className="text-secondary-600">Manage all booking requests and payments</p>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="secondary"
            leftIcon={<Download className="h-4 w-4" />}
            onClick={exportBookings}
          >
            Export
          </Button>
          <Button
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => toast.success('Create booking feature coming soon')}
          >
            Add Booking
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Total Bookings</p>
                <p className="text-2xl font-bold text-secondary-900">{bookings.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CreditCard className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Paid Bookings</p>
                <p className="text-2xl font-bold text-secondary-900">
                  {bookings.filter(b => b.paymentStatus === 'paid').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Calendar className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Pending</p>
                <p className="text-2xl font-bold text-secondary-900">
                  {bookings.filter(b => b.bookingStatus === 'pending').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CreditCard className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Total Revenue</p>
                <p className="text-2xl font-bold text-secondary-900">
                  ₵{(bookings.reduce((sum, b) => sum + (b.totalAmount || 0), 0)).toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search bookings..."
                leftIcon={<Search className="h-4 w-4" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-3">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as BookingStatus | 'all')}
                className="px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="all">All Status</option>
                <option value={'pending'}>Pending</option>
                <option value={'confirmed'}>Confirmed</option>
                <option value={'cancelled'}>Cancelled</option>
                <option value={'completed'}>Completed</option>
              </select>
              <select
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value as PaymentStatus | 'all')}
                className="px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="all">All Payments</option>
                <option value={'pending'}>Pending</option>
                <option value={'paid'}>Paid</option>
                <option value={'failed'}>Failed</option>
                <option value={'refunded'}>Refunded</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bookings Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Bookings ({filteredBookings.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-secondary-200">
                  <th className="text-left py-3 px-4 font-medium text-secondary-700">Reference</th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-700">Passenger</th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-700">Route</th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-700">Travel Date</th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-700">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-700">Payment</th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-700">Amount</th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map((booking) => (
                  <tr key={booking.id} className="border-b border-secondary-100 hover:bg-secondary-50">
                    <td className="py-3 px-4">
                      <span className="font-medium text-secondary-900">{booking.bookingReference}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-secondary-900">{booking.passengerName}</p>
                        <p className="text-sm text-secondary-500">{booking.passengerEmail}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 text-secondary-400 mr-1" />
                        <span className="text-sm">
                          {booking.route?.fromCity} → {booking.route?.toCity}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm">
                        {new Date(booking.travelDate).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <select
                        value={booking.bookingStatus}
                        onChange={(e) => updateBookingStatus(booking.id, e.target.value as BookingStatus)}
                        className={`px-2 py-1 rounded-full text-xs font-medium border-0 ${getStatusColor(booking.bookingStatus)}`}
                      >
                        <option value={'pending'}>Pending</option>
                        <option value={'confirmed'}>Confirmed</option>
                        <option value={'cancelled'}>Cancelled</option>
                        <option value={'completed'}>Completed</option>
                      </select>
                    </td>
                    <td className="py-3 px-4">
                      <select
                        value={booking.paymentStatus}
                        onChange={(e) => updatePaymentStatus(booking.id, e.target.value as PaymentStatus)}
                        className={`px-2 py-1 rounded-full text-xs font-medium border-0 ${getPaymentColor(booking.paymentStatus)}`}
                      >
                        <option value={'pending'}>Pending</option>
                        <option value={'paid'}>Paid</option>
                        <option value={'failed'}>Failed</option>
                        <option value={'refunded'}>Refunded</option>
                      </select>
                    </td>
                    <td className="py-3 px-4">
                      <span className="font-medium text-secondary-900">
                        ₵{(booking.totalAmount || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedBooking(booking);
                            setShowBookingDetail(true);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toast.success('Edit booking feature coming soon')}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteBooking(booking.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredBookings.length === 0 && (
              <div className="text-center py-12">
                <Calendar className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
                <p className="text-secondary-500">No bookings found</p>
                <p className="text-sm text-secondary-400">Try adjusting your search or filters</p>
              </div>
            )}

            {hasNext && (
              <div className="flex justify-center py-4">
                <Button
                  variant="outline"
                  onClick={loadMore}
                  loading={loadingMore}
                  disabled={loadingMore}
                >
                  Load more
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {hasNext && (
        <div className="flex justify-center">
          <Button onClick={loadMore} loading={loadingMore} variant="outline">
            Load more
          </Button>
        </div>
      )}

      {/* Booking Detail Modal */}
      {showBookingDetail && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Booking Details</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowBookingDetail(false)}
              >
                ×
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-secondary-900 mb-3">Passenger Information</h3>
                  <div className="space-y-2">
                    <p><span className="font-medium">Name:</span> {selectedBooking.passengerName}</p>
                    <p><span className="font-medium">Email:</span> {selectedBooking.passengerEmail}</p>
                    <p><span className="font-medium">Phone:</span> {selectedBooking.passengerPhone}</p>
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-secondary-900 mb-3">Booking Information</h3>
                  <div className="space-y-2">
                    <p><span className="font-medium">Reference:</span> {selectedBooking.bookingReference}</p>
                    <p><span className="font-medium">Travel Date:</span> {new Date(selectedBooking.travelDate).toLocaleDateString()}</p>
                    <p><span className="font-medium">Seat:</span> {selectedBooking.seatNumber}</p>
                  </div>
                </div>
              </div>

              {selectedBooking.route && (
                <div>
                  <h3 className="font-medium text-secondary-900 mb-3">Route Information</h3>
                  <div className="bg-secondary-50 p-4 rounded-lg">
                    <p><span className="font-medium">Route:</span> {selectedBooking.route.fromCity} → {selectedBooking.route.toCity}</p>
                    <p><span className="font-medium">Departure:</span> {selectedBooking.route.departureTime}</p>
                    <p><span className="font-medium">Arrival:</span> {selectedBooking.route.arrivalTime}</p>
                  </div>
                </div>
              )}

              <div>
                <h3 className="font-medium text-secondary-900 mb-3">Payment Information</h3>
                <div className="flex justify-between items-center">
                  <span>Total Amount:</span>
                  <span className="font-bold text-lg">₵{(selectedBooking.totalAmount || 0).toFixed(2)}</span>
                </div>
              </div>

              {selectedBooking.notes && (
                <div>
                  <h3 className="font-medium text-secondary-900 mb-3">Notes</h3>
                  <p className="text-secondary-600">{selectedBooking.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
